import React, { forwardRef, useCallback, useRef } from 'react';
import classNames from 'classnames';
import { ImageAvatar } from '@components/avatar';
import { Icon, IconName, IconSize } from '@components/icon';
import './NavItem.scss';

/**
 * Props NavItem forwards to the polymorphic link root (`as`, default native `a`).
 */
export interface NavItemForwardedLinkProps
  extends Pick<
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
    'className' | 'target' | 'tabIndex' | 'aria-current' | 'aria-label'
  > {
  href: string;
  onClick?: React.MouseEventHandler<Element>;
  children: React.ReactNode;
}

export interface NavItemProps {
  children?: React.ReactNode;
  /** Text of the NavItem */
  label?: string;
  /** Provider logo source */
  logoSrc?: string;
  /** Alt text for provider logo */
  logoAlt?: string;
  /** Image for ImageAvatar component */
  src?: string;
  /** Alt tag for ImageAvatar component */
  alt?: string;
  /** Icon for Icon component */
  icon?: IconName;
  /** If true, the chevron is displayed */
  dropdown?: boolean;
  /** If true, use bold text */
  active?: boolean;
  /** If true, chevron changes direction */
  isOpen?: boolean;
  /** Change size of layout for desktop and mobile. Default large */
  size?: 'large' | 'small';
  dropdownIconSize?: IconSize;
  /** Choose alternate color scheme. Default primary */
  variant?: 'primary' | 'secondary';
  className?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  /** URL for the anchor tag */
  href?: string;
  /** Polymorphic link root. Default: native `a` */
  as?: React.ElementType;
  /** Target for the anchor tag */
  target?: '_blank' | '_self' | '_parent' | '_top' | '_unfencedTop';
  /** Aria label for the nav item */
  ariaLabel?: string;
  /** role for the nav item */
  role?: string;
  /** Truncate the label with ellipsis when an icon/avatar/dropdown chevron is present. Default true. */
  truncateLabel?: boolean;
}

/**
 * NavItem Component
 * @param children
 * @param label - Text of the NavItem
 * @param logoSrc - provider logo source
 * @param logoAlt - Alt text for provider logo
 * @param src - Image for ImageAvatar component
 * @param alt - Alt tag for ImageAvatar component
 * @param icon - Icon for Icon component
 * @param dropdown - If true, the chevron is displayed
 * @param active - If true, use bold text
 * @param isOpen - if true, chevron changes direction
 * @param size - change size of layout for desktop and mobile
 * @param variant - Choose alternate color scheme
 * @param onClick - a click event callback
 * @param className - Additional classnames for the component.
 * @param href - URL for the anchor tag
 * @param as - Polymorphic link root (default `a`)
 * @param target - Target for the anchor tag
 * @param dropdownIconSize - size of the dropdown icon
 * @param ariaLabel - Aria label for the nav item
 * @param role - role for the nav item
 * @param truncateLabel - Truncate the label with ellipsis when an icon/avatar/dropdown chevron is present. Default true.
 * @constructor
 */
export const NavItem = forwardRef<HTMLElement, NavItemProps>(
  (
    {
      children,
      label,
      logoSrc,
      logoAlt,
      src,
      alt = 'Profile Image',
      icon,
      dropdown = false,
      active = false,
      isOpen = false,
      size = 'large',
      variant = 'primary',
      className,
      onClick,
      href,
      as: LinkWrapper = 'a',
      target,
      dropdownIconSize = 'small',
      ariaLabel,
      truncateLabel = true,
      ...rest
    },
    ref,
  ) => {
    const divRef = useRef<HTMLDivElement | null>(null);
    const dropdownIcon = isOpen ? 'chevron-upward' : 'chevron-downward';
    const hasIcon = !!(dropdown || icon || src);
    const isCurrentPage = !!href && active;

    const setDivRef = useCallback(
      (el: HTMLDivElement | null) => {
        divRef.current = el;
        if (typeof ref === 'function') return ref(el);
        if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = el;
      },
      [ref],
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        divRef.current?.click();
      }
    };

    const containerClass = classNames(
      'vds-nav-item',
      `vds-nav-item--${size}`,
      {
        'vds-nav-item--active': active,
        'vds-nav-item--secondary': variant === 'secondary',
      },
      className,
    );

    const content = (
      <>
        {logoSrc && <img className="vds-nav-item__logo" src={logoSrc} alt={logoAlt} />}
        {src && <ImageAvatar alt={alt} className="vds-nav-item__avatar" size="4xs" src={src} />}
        {icon && !src && <Icon className="vds-nav-item__icon" name={icon} />}
        {label && (
          <span
            className={classNames('vds-nav-item__label', {
              'vds-nav-item__avatar-label': hasIcon && truncateLabel,
            })}>
            {label}
          </span>
        )}
        {children}
        {dropdown && (
          <Icon className="vds-nav-item__chevron" name={dropdownIcon} size={dropdownIconSize} />
        )}
      </>
    );

    if (href) {
      return (
        <LinkWrapper
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          target={target}
          className={containerClass}
          onClick={onClick}
          aria-current={isCurrentPage ? 'page' : undefined}
          aria-label={ariaLabel}
          {...rest}>
          {content}
        </LinkWrapper>
      );
    }

    return (
      <div
        ref={setDivRef}
        className={containerClass}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        aria-label={ariaLabel}
        {...rest}>
        {content}
      </div>
    );
  },
);
